import { Alert, Button, Form, Input } from 'antd';
import { Lock, ShieldCheck, ShieldOff } from 'lucide-react';
import { useState } from 'react';

interface TotpStatusCardProps {
  enabled: boolean;
  error: string | null;
  submitting: boolean;
  onEnable: () => void;
  onDisable: (password: string) => Promise<boolean>;
}

/** The at-rest view: on or off, with the one action available from here. Turning off asks for the password inline. */
export function TotpStatusCard({ enabled, error, submitting, onEnable, onDisable }: TotpStatusCardProps) {
  const [confirmingDisable, setConfirmingDisable] = useState(false);

  async function submitDisable(values: { password: string }) {
    if (await onDisable(values.password)) setConfirmingDisable(false);
  }

  return (
    <div className="rounded-lg border border-line bg-surface p-6">
      <div className="flex items-start gap-3">
        <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/20">
          {enabled ? <ShieldCheck className="size-4.5" /> : <ShieldOff className="size-4.5" />}
        </div>
        <div>
          <h2 className="text-base font-semibold text-fg">Two-factor authentication is {enabled ? 'on' : 'off'}</h2>
          <p className="mt-1 text-sm text-muted">
            {enabled ? 'A code from your authenticator app is required at sign-in.' : 'Add a code from an authenticator app as a second step at sign-in.'}
          </p>
        </div>
      </div>

      {!enabled && (
        <Button className="mt-5" type="primary" onClick={onEnable}>
          Turn on
        </Button>
      )}

      {enabled && !confirmingDisable && (
        <Button className="mt-5" danger onClick={() => setConfirmingDisable(true)}>
          Turn off
        </Button>
      )}

      {enabled && confirmingDisable && (
        <div className="mt-5 max-w-xs">
          {error && <Alert className="mb-4" type="error" title={error} showIcon />}
          <Form<{ password: string }> layout="vertical" requiredMark={false} onFinish={submitDisable} disabled={submitting}>
            <Form.Item name="password" label="Confirm your password" rules={[{ required: true }]}>
              <Input.Password prefix={<Lock className="size-4 text-muted" />} autoComplete="current-password" autoFocus />
            </Form.Item>
            <div className="flex gap-2">
              <Button danger htmlType="submit" loading={submitting}>
                Turn off
              </Button>
              <Button onClick={() => setConfirmingDisable(false)} disabled={submitting}>
                Cancel
              </Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
}
